// components/admin/PublicationList.js
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getPublications,
  createPublication,
  updatePublication,
  deletePublication
} from '../../../redux/actions/publicationActions';
import { FiSearch, FiEdit2, FiTrash2, FiPlus, FiToggleLeft } from 'react-icons/fi';
import { toast } from 'sonner';
import { handleImageUploadCloudOnly } from '../../../actions/upload/UploadCloud';
import AdminLayout from '../../../components/layout/AdminLayout';
import AddPublicationModal from '../../../modals/publications/AddPublicationModal';
import EditPublicationModal from '../../../modals/publications/EditPublicationModal';
import DeletePublicationModal from '../../../modals/publications/DeletePublicationModal';

const PublicationList = () => {
  const dispatch = useDispatch();

  const { publications, loading, error } = useSelector((state) => state.publications);

  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPub, setSelectedPub] = useState(null);

  const [newPub, setNewPub] = useState({
    title: '',
    content: '',
    type: 'promo',
    status: 'active',
    startDate: '',
    endDate: '',
    linkUrl: '',
    imageUrl: ''
  });

  const [imageFile, setImageFile] = useState(null);

  // Charger les pubs selon filtres
  useEffect(() => {
    const filters = {
      ...(searchTerm && { searchTerm }),
      ...(startDate && { startDate }),
      ...(endDate && { endDate })
    };
    dispatch(getPublications(filters));
  }, [dispatch, searchTerm, startDate, endDate]);

  const filteredPubs = (publications || []).filter(pub =>
    pub.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    pub.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    pub.content?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Gestion du fichier image
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) setImageFile(file);
    // let imageUrl = newPub.imageUrl;

    if (imageFile) {
      const url = await handleImageUploadCloudOnly(imageFile);
      if (!url) { toast.error("Échec de l'upload de l'image"); return; }
      setNewPub({ ...newPub, imageUrl: url });
    }
  };

  // Créer une nouvelle publication
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newPub.title || !newPub.content || !newPub.startDate || !newPub.endDate || !newPub.imageUrl) {
      toast.warn('Veuillez remplir tous les champs obligatoires.');
      return;
    }



    try {
      // ✅ Ajout de `await`
      await dispatch(createPublication({
        ...newPub,
        imageUrl: newPub.imageUrl,
        status: 'active'
      }));

      setShowAddModal(false);
      resetForm();
    } catch (err) {
      console.error("Erreur lors de la création :", err);
      toast.error("Échec de la création de la publication");
    }
  };

  const resetForm = () => {
    setNewPub({
      title: '', content: '', type: 'promo', startDate: '', endDate: '', linkUrl: '', imageUrl: ''
    });
    setImageFile(null);
  };

  // Préparer édition
  const handleEdit = (pub) => {
    setNewPub({
      title: pub.title,
      content: pub.content,
      type: pub.type,
      status: pub.status,
      startDate: new Date(pub.startDate).toISOString().split('T')[0],
      endDate: new Date(pub.endDate).toISOString().split('T')[0],
      linkUrl: pub.linkUrl,
      imageUrl: pub.imageUrl
    });
    setSelectedPub(pub);
    setShowEditModal(true);
  };

  const handleUpdate = async () => {
    if (!newPub.title || !newPub.content || !newPub.startDate || !newPub.endDate || !newPub.imageUrl) {
      toast.warn('Veuillez remplir tous les champs.');
      return;
    }

    try {
      await dispatch(updatePublication(selectedPub._id, {
        ...newPub,
        imageUrl: newPub.imageUrl,
      }));
      setShowEditModal(false);
      setSelectedPub(null);
      resetForm();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = (pub) => {
    setSelectedPub(pub);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await dispatch(deletePublication(selectedPub._id));
      setShowDeleteModal(false);
      setSelectedPub(null);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <AdminLayout>
      {/* Titre + Bouton Ajouter */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="h4 fw-bold">Gestion des Pubs</h1>
           <div>
          <p className="text-muted mb-0">
            Gérer les publicités affichées sur le site.
          </p>
        </div>
        </div>
        
       
        <button className="btn btn-danger" onClick={() => setShowAddModal(true)}>
          <FiPlus size={18} className="me-2" /> Ajouter une Pub
        </button>
      </div>

      {/* Filtres */}
      <div className="card mb-4 shadow-sm">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <label>Date début</label>
              <input
                type="date"
                className="form-control"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label>Date fin</label>
              <input
                type="date"
                className="form-control"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <label>Rechercher</label>
              <div className="input-group">
                <span className="input-group-text"><FiSearch /></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Titre, contenu..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {loading ? (
        <div className="d-flex justify-content-center my-5 py-5">
          <div className="spinner-border text-danger" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
        </div>
      ) : (
        <div className="card shadow-sm">
          {filteredPubs.length === 0 ? (
            <div className="card-body text-center text-muted">
              Aucune publication trouvée.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0 table-bordered">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '50px' }}>Image</th>
                    <th style={{minWidth:"100px"  ,maxWidth: '150px' }}>Titre</th>
                    <th style={{width: '300px' }}>Titre</th>
                    <th>Type</th>
                    <th>Statut</th>
                    <th>Début</th>
                    <th>Fin</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPubs.map((pub) => (
                    <tr key={pub._id}>
                      <td>
                        {pub.imageUrl ? (
                          <img src={pub.imageUrl} alt="" style={{ height: '50px', width: '100px', objectFit: 'cover', borderRadius: '4px' }} />
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>{pub.title|| ""}</td>
                      <td>{pub.content|| ""}</td>
                      <td><span className="badge bg-info">{pub.type}</span></td>
                      <td>
                        <span className={`badge ${pub.status === 'active' ? 'bg-success' : 'bg-secondary'}`}>
                          {pub.status === 'active' ? 'Actif' : 'Inactif'}
                        </span>
                      </td>
                      <td>{new Date(pub.startDate).toLocaleDateString('fr-FR')}</td>
                      <td>{new Date(pub.endDate).toLocaleDateString('fr-FR')}</td>
                      <td className="text-end">
                        <button className="btn btn-sm btn-outline-warning me-2" onClick={() => handleEdit(pub)}>
                          <FiEdit2 size={16} />
                        </button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(pub)}>
                          <FiTrash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modales */}
      <AddPublicationModal
        show={showAddModal}
        onHide={() => {
          setShowAddModal(false);
          resetForm();
          setImageFile(null);
        }}
        onSubmit={handleSubmit}
        newPub={newPub}
        handleChange={(e) => setNewPub({ ...newPub, [e.target.name]: e.target.value })}
        handleFileChange={handleFileChange}
      />

      <EditPublicationModal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setSelectedPub(null);
          resetForm();
          setImageFile(null);
        }}
        onUpdate={handleUpdate}
        pub={selectedPub}
        newPub={newPub}
        handleChange={(e) => setNewPub({ ...newPub, [e.target.name]: e.target.value })}
        handleFileChange={handleFileChange}
      />

      <DeletePublicationModal
        show={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
          setSelectedPub(null);
        }}
        onDelete={confirmDelete}
        pubName={selectedPub?.title}
      />
    </AdminLayout>
  );
};

export default PublicationList;